import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';

interface Jour {
  jour: string;
  incidents: number;
  confirmations_mesurees: number;
  retablissements_mesures: number;
  confirmation_heures: number | null;
  retablissement_heures: number | null;
}
export function EvolutionReseaux({
  parametres,
  contexte,
}: {
  parametres: {
    debut: string;
    fin: string;
    province_id?: string;
    domaine?: string;
    inclure_fictives: string;
  };
  contexte: string;
}) {
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const q = useQuery({
    queryKey: ['evolution-reseaux', contexte, parametres],
    queryFn: () =>
      requete<{ elements: Jour[]; calcule_le: string }>('/supervision/reseaux/evolution', {
        parametres,
      }),
  });
  const jours = q.data?.elements ?? [];
  const maximum = Math.max(1, ...jours.map((j) => j.incidents));
  const x = (i: number) => (jours.length === 1 ? 330 : 50 + (i * 550) / (jours.length - 1));
  const y = (n: number) => 220 - (n * 170) / maximum;
  const duree = (n: number | null) =>
    n === null ? 'Non mesuré' : n.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) + ' h';
  return (
    <section aria-label="Évolution des incidents enregistrés">
      <h3>Évolution des incidents enregistrés</h3>
      <p>
        Nombre de dossiers créés chaque jour, en heure de Libreville, dans le périmètre filtré. Zéro
        signifie aucun dossier enregistré, sans garantir une absence de panne. Les délais du tableau
        concernent ces mêmes cohortes et évoluent avec leur traitement.
      </p>
      {parametres.inclure_fictives === 'oui' && <p>Données de démonstration incluses.</p>}
      {(q.error || erreur) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {q.isPending && <p>Chargement de l’évolution…</p>}
      {q.data && (
        <>
          <p>
            Calculé le {new Date(q.data.calcule_le).toLocaleString('fr-FR')} —{' '}
            {jours.reduce((s, j) => s + j.incidents, 0)} dossiers sur {jours.length} jours.
          </p>
          <svg
            role="img"
            aria-label="Courbe du nombre quotidien de dossiers créés ; valeurs disponibles dans le tableau ci-dessous"
            viewBox="0 0 640 270"
            style={{
              width: '100%',
              maxWidth: 900,
              display: 'block',
              color: 'var(--cc-encre)',
              fontSize: 22,
            }}
          >
            <path d="M50 50 V220 H600" fill="none" stroke="currentColor" />
            <text x="50" y="35" fill="currentColor">
              {maximum}
            </text>
            <text x="40" y="224" textAnchor="end" fill="currentColor">
              0
            </text>
            <text x="50" y="250" fill="currentColor">
              {parametres.debut}
            </text>
            <text x="600" y="250" textAnchor="end" fill="currentColor">
              {parametres.fin}
            </text>
            <polyline
              points={jours.map((j, i) => `${x(i)},${y(j.incidents)}`).join(' ')}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
            {jours.map((j, i) => (
              <circle
                key={j.jour}
                cx={x(i)}
                cy={y(j.incidents)}
                r={jours.length > 60 ? 2 : 4}
                fill="currentColor"
              >
                <title>
                  {j.jour} : {j.incidents} dossiers
                </title>
              </circle>
            ))}
          </svg>
          {(['csv', 'xlsx', 'pdf'] as const).map((format) => (
            <button
              key={format}
              disabled={occupe || q.isFetching}
              onClick={async () => {
                setOccupe(true);
                setErreur('');
                try {
                  const r = await requete<Response>(
                    format === 'pdf'
                      ? '/supervision/reseaux/evolution/rapport.pdf'
                      : '/supervision/reseaux/evolution/export',
                    {
                      parametres: { ...parametres, ...(format === 'pdf' ? {} : { format }) },
                      brut: true,
                    },
                  );
                  const url = URL.createObjectURL(await r.blob());
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `evolution-reseaux.${format}`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch (e) {
                  setErreur(messageErreur(e));
                } finally {
                  setOccupe(false);
                }
              }}
            >
              Exporter l’évolution {format === 'csv' ? 'CSV' : format === 'pdf' ? 'PDF' : 'Excel'}
            </button>
          ))}
          {occupe && <p role="status">Préparation du téléchargement de l’évolution…</p>}
          <details>
            <summary>Consulter les valeurs journalières ({jours.length} jours)</summary>
            <div
              role="region"
              aria-label="Tableau de l’évolution"
              tabIndex={0}
              style={{ overflowX: 'auto' }}
            >
              <table>
                <caption>Incidents créés et délais mesurés par jour de création</caption>
                <thead>
                  <tr>
                    {[
                      'Jour',
                      'Incidents créés',
                      'Confirmations mesurées',
                      'Délai de confirmation',
                      'Rétablissements mesurés',
                      'Délai de rétablissement',
                    ].map((t) => (
                      <th scope="col" key={t}>
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {jours.map((j) => (
                    <tr key={j.jour}>
                      <th scope="row">{j.jour}</th>
                      <td>{j.incidents}</td>
                      <td>{j.confirmations_mesurees}</td>
                      <td>{duree(j.confirmation_heures)}</td>
                      <td>{j.retablissements_mesures}</td>
                      <td>{duree(j.retablissement_heures)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  );
}
